/**
 * Le bouche-à-oreille de /outils : ce qu'on envoie à un ami, et la carte qui
 * l'accompagne.
 *
 * UN MESSAGE QUI DIT CE QU'ON A DÉCOUVERT, PAS « C'EST GRATUIT ». « J'ai vu en
 * trente secondes ce qui m'attend » ne donnait à l'ami aucune raison de
 * cliquer. On choisit ici la découverte la plus parlante de la situation (une
 * date de TVA, la fin de l'ACRE, le plafond qui approche, une option d'impôt
 * qui ferait payer moins) et on la dit à la première personne, au tutoiement :
 * c'est le lecteur qui écrit à un ami, pas Believemy.
 *
 * AUCUN MONTANT, JAMAIS. Ni revenu, ni gain, ni chiffre d'affaires : des
 * dates et des pourcentages, comme sur la carte. On publie le message sans
 * rien dévoiler de ce qu'on gagne.
 *
 * Module pur : il se teste seul (scripts/check-dossier.mjs).
 */

const MOIS = {
    fr: ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"],
    en: ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"],
};

/** « 1er décembre », « 15 mars » ; « 1 December » en anglais. */
function dateDite(iso, lang) {
    const [, m, j] = iso.split("-").map(Number);
    if (lang === "en") return `${j} ${MOIS.en[m - 1]}`;
    return `${j === 1 ? "1er" : j} ${MOIS.fr[m - 1]}`;
}

const TEXTES = {
    fr: {
        tva: "Ma TVA arrive le {date}, je ne l'aurais pas vu venir. Regarde quand arrive la tienne, c'est gratuit :",
        tvaJanvier: "Je passe à la TVA au 1er janvier, je ne l'avais pas vu venir. Regarde où tu en es, c'est gratuit :",
        acre: "Mon ACRE s'arrête le {date} : mes cotisations vont remonter. Regarde tes propres dates, c'est gratuit :",
        plafond: "Je suis déjà à {part} du plafond de la micro. Regarde où tu en es, c'est gratuit :",
        vfl: "Je viens de voir qu'une option d'impôt me ferait payer moins. Regarde si c'est ton cas, c'est gratuit :",
        projet: "J'ai vu d'un coup ce qui m'attend si je me lance : TVA, cotisations, dates. Regarde pour ton projet, c'est gratuit :",
        defaut: "J'ai vu d'un coup mes prochaines dates de solopreneur : TVA, CFE, impôt. Regarde les tiennes, c'est gratuit :",
    },
    en: {
        tva: "My VAT kicks in on {date}, I wouldn't have seen it coming. Check when yours does, it's free:",
        tvaJanvier: "I switch to VAT on 1 January, I hadn't seen it coming. Check where you stand, it's free:",
        acre: "My ACRE ends on {date}: my contributions are going back up. Check your own dates, it's free:",
        plafond: "I'm already at {part} of the micro ceiling. Check where you stand, it's free:",
        vfl: "I just found out a tax option would make me pay less. Check if it's the same for you, it's free:",
        projet: "I saw at a glance what's ahead if I start out: VAT, contributions, dates. Check for your project, it's free:",
        defaut: "I saw at a glance my next dates as a solopreneur: VAT, CFE, income tax. Check yours, it's free:",
    },
};

/**
 * La découverte la plus parlante, dans cet ordre : la TVA qui arrive (c'est
 * elle qui change le plus la vie d'un solopreneur), la fin de l'ACRE, le
 * plafond qui approche, l'option d'impôt, et sinon les dates.
 *
 * Un projet passe avant tout : « je passe à la TVA » sonne faux chez qui
 * n'a encore rien déclaré. `projet` le dit quand la situation a été calculée
 * comme une micro (l'entrée de /outils le fait pour un projet).
 *
 * @param r la situation (situationDuDossier)
 * @returns {{ cle: string, texte: string }}
 */
export function messagePartage(r, lang = "fr", { projet = false } = {}) {
    const l = lang === "en" ? "en" : "fr";
    const t = TEXTES[l];
    const dire = (cle, valeurs = {}) => ({ cle, texte: t[cle].replace(/\{(\w+)\}/g, (_, k) => valeurs[k] ?? "") });

    if (projet || r.dossier?.statut === "projet") return dire("projet");

    if (r.tva?.statut === "assujetti_immediat" && r.tva.date && !r.tva.passe && r.tva.joursRestants <= 365) {
        return dire("tva", { date: dateDite(r.tva.date, l) });
    }
    if (r.tva?.statut === "bascule_janvier_prochain") return dire("tvaJanvier");
    if (r.acre?.obtenue && r.acre.enCours) {
        const jours = Math.round((new Date(`${r.acre.fin}T12:00:00`) - new Date(`${r.aujourdhui}T12:00:00`)) / 86400000);
        if (jours >= 0 && jours <= 200) return dire("acre", { date: dateDite(r.acre.fin, l) });
    }
    if (r.plafond && r.plafond.part >= 0.75 && r.plafond.part < 1) {
        return dire("plafond", { part: `${Math.round(r.plafond.part * 100)}${l === "fr" ? " " : " "}%` });
    }
    if (r.vfl && r.vfl.option !== "vfl" && r.vfl.gain >= 150) return dire("vfl");
    return dire("defaut");
}

/** L'adresse partagée : /outils, vide, marquée pour accueillir celui qui arrive. */
export function lienPartage(origine, lang = "fr", source = "carte") {
    return `${origine}${lang === "en" ? "/en/tools" : "/fr/outils"}?utm_source=${source}&utm_medium=partage`;
}

/** Les sources qui disent qu'un ami a envoyé le lien. */
export const SOURCES_AMI = ["carte", "ami"];

/**
 * Les paramètres de la carte (app/api/og/situation), tirés de la situation :
 * aucun montant n'y entre. `v` : la version du dessin. L'image se garde un
 * jour dans le cache ; changer de dessin change d'adresse, et personne ne
 * voit l'ancienne.
 */
export function parametresCarte(r, part, lang) {
    const p = new URLSearchParams({ locale: lang, v: "3" });
    const d = r.dossier;
    if (part !== null && part !== undefined) p.set("part", String(Math.round(part * 100)));
    if (d.statut) p.set("statut", d.statut);
    if (r.tva) {
        if (r.tva.statut === "deja") p.set("tva", "deja");
        else if (r.tva.statut === "franchise") p.set("tva", "sous");
        else if (r.tva.statut === "assujetti_immediat" && !r.tva.passe && r.tva.joursRestants >= 0) p.set("tva", String(Math.min(400, r.tva.joursRestants)));
    }
    if (r.plafond) p.set("plafond", String(Math.min(100, Math.round(r.plafond.part * 100))));
    const dates = r.echeances.length;
    if (dates > 0) p.set("dates", String(Math.min(20, dates)));
    // Les trois prochaines : une date et une clé, jamais un montant.
    if (dates > 0) p.set("prochaines", r.echeances.slice(0, 3).map((e) => `${e.date}.${e.cle}`).join("~"));
    if (d.statut === "projet") p.set("mois", "projet");
    else if (d.dateDebut) {
        const [a, m] = d.dateDebut.split("-").map(Number);
        const [aa, mm] = r.aujourdhui.split("-").map(Number);
        const mois = (aa - a) * 12 + (mm - m);
        if (mois >= 0) p.set("mois", String(Math.min(600, mois)));
    }
    return p.toString();
}
